import React, { useState, useEffect, useCallback } from 'react';
import {
    Car, Search, Plus, X, Trash2,
    ChevronLeft, ChevronRight, Loader2, AlertCircle,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    fetchFleetVehicles,
    createFleetVehicle,
    deleteFleetVehicle,
    setFleetVehicleRegoFilter,
    clearFleetVehicleErrors,
    selectFleetVehicles,
    selectFleetVehiclesLoading,
    selectFleetVehiclesError,
    selectFleetVehiclesPage,
    selectFleetVehiclesTotalPages,
    selectFleetVehiclesTotalElements,
    selectFleetVehicleCreating,
    selectFleetVehicleCreateError,
    selectFleetVehicleDeletingUuid,
    selectFleetVehicleDeleteError,
} from '../store/slices/fleetVehicleSlice';
import Layout from './Layout';
import { AU_STATES } from '../constants/auStates';

const PAGE_SIZE = 20;


// ============================================
// Add Vehicle dialog
// ============================================

const AddVehicleDialog = ({ open, onClose, onSubmit, submitting, error }) => {
    const [form, setForm] = useState({
        make: '', model: '', year: '', rego: '', regoState: 'VIC',
    });
    const [localError, setLocalError] = useState(null);

    useEffect(() => {
        if (open) {
            setForm({ make: '', model: '', year: '', rego: '', regoState: 'VIC' });
            setLocalError(null);
        }
    }, [open]);

    if (!open) return null;

    const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

    const handleSubmit = () => {
        setLocalError(null);

        if (!form.make.trim()) return setLocalError('Enter the make.');
        if (!form.model.trim()) return setLocalError('Enter the model.');

        const year = parseInt(form.year, 10);
        const thisYear = new Date().getFullYear();
        if (!form.year || Number.isNaN(year) || year < 1900 || year > thisYear + 1) {
            return setLocalError(`Enter a valid year (1900–${thisYear + 1}).`);
        }
        if (!form.rego.trim()) return setLocalError('Enter the registration number.');

        onSubmit({
            make: form.make.trim(),
            model: form.model.trim(),
            year,
            rego: form.rego.trim(),
            regoState: form.regoState,
        });
    };

    const inputCls =
        'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={submitting ? undefined : onClose} />

            <div className="relative bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                            <Car size={18} className="text-emerald-600" />
                        </div>
                        <h2 className="text-lg font-display font-semibold text-gray-900">
                            Add vehicle
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <p className="text-sm text-gray-500">
                        Add a vehicle to your fleet so it's ready to book in for a service.
                    </p>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Make</label>
                            <input
                                type="text"
                                value={form.make}
                                onChange={set('make')}
                                placeholder="Toyota"
                                className={inputCls}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Model</label>
                            <input
                                type="text"
                                value={form.model}
                                onChange={set('model')}
                                placeholder="HiLux"
                                className={inputCls}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Year</label>
                        <input
                            type="number"
                            value={form.year}
                            onChange={set('year')}
                            placeholder="2021"
                            className={inputCls}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Registration
                            </label>
                            <input
                                type="text"
                                value={form.rego}
                                onChange={set('rego')}
                                placeholder="ABC123"
                                className={`${inputCls} uppercase`}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">State</label>
                            <select
                                value={form.regoState}
                                onChange={set('regoState')}
                                className={inputCls}
                            >
                                {AU_STATES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {(localError || error) && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-600">{localError || error}</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold cursor-pointer disabled:opacity-60 border-none"
                    >
                        {submitting && <Loader2 size={16} className="animate-spin" />}
                        {submitting ? 'Adding…' : 'Add vehicle'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// Confirm removal dialog
// ============================================

const ConfirmRemoveDialog = ({ open, vehicle, onCancel, onConfirm, submitting, error }) => {
    if (!open || !vehicle) return null;

    const label = vehicle.displayName
        || [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')
        || vehicle.rego;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={submitting ? undefined : onCancel} />

            <div className="relative bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center border border-red-100">
                            <Trash2 size={18} className="text-red-600" />
                        </div>
                        <h2 className="text-lg font-display font-semibold text-gray-900">
                            Remove vehicle
                        </h2>
                    </div>
                    <button
                        onClick={onCancel}
                        disabled={submitting}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <p className="text-sm text-gray-600">
                        Remove <span className="font-semibold text-gray-900">{label}</span> from your fleet?
                    </p>

                    {error && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                    <button
                        onClick={onCancel}
                        disabled={submitting}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm font-semibold cursor-pointer disabled:opacity-60 border-none"
                    >
                        {submitting && <Loader2 size={16} className="animate-spin" />}
                        {submitting ? 'Removing…' : 'Remove vehicle'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// My Vehicles page (fleet portal)
// ============================================

const FleetVehiclesPage = () => {
    const dispatch = useAppDispatch();

    const vehicles = useAppSelector(selectFleetVehicles);
    const loading = useAppSelector(selectFleetVehiclesLoading);
    const error = useAppSelector(selectFleetVehiclesError);
    const page = useAppSelector(selectFleetVehiclesPage);
    const totalPages = useAppSelector(selectFleetVehiclesTotalPages);
    const totalElements = useAppSelector(selectFleetVehiclesTotalElements);
    const creating = useAppSelector(selectFleetVehicleCreating);
    const createError = useAppSelector(selectFleetVehicleCreateError);
    const deletingUuid = useAppSelector(selectFleetVehicleDeletingUuid);
    const deleteError = useAppSelector(selectFleetVehicleDeleteError);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [regoInput, setRegoInput] = useState('');
    const [vehicleToRemove, setVehicleToRemove] = useState(null);

    const load = useCallback((opts = {}) => {
        dispatch(fetchFleetVehicles({
            page: opts.page ?? 0,
            size: PAGE_SIZE,
            rego: opts.rego ?? regoInput,
        }));
    }, [dispatch, regoInput]);

    // Initial load.
    useEffect(() => {
        dispatch(fetchFleetVehicles({ page: 0, size: PAGE_SIZE, rego: '' }));
    }, [dispatch]);

    // Debounced rego filter.
    useEffect(() => {
        const t = setTimeout(() => {
            dispatch(setFleetVehicleRegoFilter(regoInput));
            dispatch(fetchFleetVehicles({ page: 0, size: PAGE_SIZE, rego: regoInput }));
        }, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regoInput]);

    const handleCreate = async (payload) => {
        const result = await dispatch(createFleetVehicle(payload));
        if (createFleetVehicle.fulfilled.match(result)) {
            setDialogOpen(false);
            dispatch(clearFleetVehicleErrors());
            load({ page: 0 });
        }
    };

    const askRemove = (vehicle) => {
        dispatch(clearFleetVehicleErrors());
        setVehicleToRemove(vehicle);
    };

    const confirmRemove = async () => {
        if (!vehicleToRemove) return;

        const result = await dispatch(deleteFleetVehicle(vehicleToRemove.uuid));
        if (deleteFleetVehicle.fulfilled.match(result)) {
            setVehicleToRemove(null);
            load({ page });
        }
        // On failure the dialog stays open and renders deleteError — this is how
        // the "vehicle has service history" rejection from the backend surfaces.
    };

    const goToPage = (target) => {
        if (target < 0 || target >= totalPages) return;
        dispatch(fetchFleetVehicles({ page: target, size: PAGE_SIZE, rego: regoInput }));
    };

    return (
        <Layout>
            <div className="px-6 py-8">
                {/* Page header */}
                <div className="flex items-start justify-between mb-6 gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                            <Car size={22} className="text-emerald-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-gray-900">
                                My Vehicles
                            </h1>
                            <p className="text-sm text-gray-500 mt-0.5">
                                Manage the vehicles in your fleet.
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={() => { dispatch(clearFleetVehicleErrors()); setDialogOpen(true); }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold cursor-pointer border-none flex-shrink-0"
                    >
                        <Plus size={18} />
                        <span className="hidden sm:inline">Add vehicle</span>
                        <span className="sm:hidden">Add</span>
                    </button>
                </div>

                {/* Table card */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    {/* Rego filter */}
                    <div className="p-4 border-b border-gray-100">
                        <div className="relative max-w-md">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={regoInput}
                                onChange={(e) => setRegoInput(e.target.value)}
                                placeholder="Filter by registration"
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent uppercase"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="py-16 flex flex-col items-center justify-center">
                            <Loader2 size={28} className="text-emerald-500 animate-spin" />
                            <p className="mt-3 text-sm text-gray-500">Loading vehicles…</p>
                        </div>
                    ) : error ? (
                        <div className="py-16 flex flex-col items-center justify-center px-6 text-center">
                            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
                                <AlertCircle size={22} className="text-red-500" />
                            </div>
                            <p className="text-sm text-gray-900 font-medium">Couldn't load vehicles</p>
                            <p className="text-sm text-gray-500 mt-1">{error}</p>
                            <button
                                onClick={() => load({ page: 0 })}
                                className="mt-4 px-4 py-2 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer border-none"
                            >
                                Try again
                            </button>
                        </div>
                    ) : vehicles.length === 0 ? (
                        <div className="py-16 flex flex-col items-center justify-center px-6 text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                <Car size={22} className="text-gray-400" />
                            </div>
                            <p className="text-sm text-gray-900 font-medium">
                                {regoInput ? 'No vehicles match that registration' : 'No vehicles yet'}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                {regoInput
                                    ? 'Try a different registration number.'
                                    : 'Add your first vehicle to start booking services.'}
                            </p>
                            {!regoInput && (
                                <button
                                    onClick={() => { dispatch(clearFleetVehicleErrors()); setDialogOpen(true); }}
                                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer border-none"
                                >
                                    <Plus size={16} />
                                    Add vehicle
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Registration</th>
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Make</th>
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Model</th>
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Year</th>
                                        <th className="px-6 py-3 w-12" />
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                    {vehicles.map((v) => (
                                        <tr key={v.uuid} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                    <span className="inline-flex items-center px-2.5 py-1 bg-gray-100 rounded-lg text-sm font-semibold text-gray-900 tracking-wide">
                                                        {v.rego || '—'}
                                                    </span>
                                                {v.regoState && (
                                                    <span className="ml-2 text-xs text-gray-400">{v.regoState}</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{v.make}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{v.model}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{v.year}</td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => askRemove(v)}
                                                    disabled={deletingUuid === v.uuid}
                                                    title="Remove vehicle"
                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                                                >
                                                    {deletingUuid === v.uuid
                                                        ? <Loader2 size={16} className="animate-spin" />
                                                        : <Trash2 size={16} />}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="sm:hidden divide-y divide-gray-50">
                                {vehicles.map((v) => (
                                    <div key={v.uuid} className="px-4 py-4 flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 rounded-lg text-sm font-semibold text-gray-900">
                                                {v.rego || '—'}
                                            </span>
                                            <p className="text-sm text-gray-600 mt-1.5">
                                                {v.year} {v.make} {v.model}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => askRemove(v)}
                                            disabled={deletingUuid === v.uuid}
                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50 flex-shrink-0"
                                        >
                                            {deletingUuid === v.uuid
                                                ? <Loader2 size={16} className="animate-spin" />
                                                : <Trash2 size={16} />}
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                                <p className="text-sm text-gray-500">
                                    {totalElements} {totalElements === 1 ? 'vehicle' : 'vehicles'}
                                </p>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => goToPage(page - 1)}
                                            disabled={page <= 0}
                                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <span className="text-sm text-gray-600 px-2">
                                            Page {page + 1} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => goToPage(page + 1)}
                                            disabled={page >= totalPages - 1}
                                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <AddVehicleDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleCreate}
                submitting={creating}
                error={createError}
            />

            <ConfirmRemoveDialog
                open={!!vehicleToRemove}
                vehicle={vehicleToRemove}
                onCancel={() => {
                    setVehicleToRemove(null);
                    dispatch(clearFleetVehicleErrors());
                }}
                onConfirm={confirmRemove}
                submitting={deletingUuid === vehicleToRemove?.uuid}
                error={deleteError}
            />
        </Layout>
    );
};

export default FleetVehiclesPage;